/**
 * WidgetLine — recharts LineChart with optional overlay series.
 */

import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend } from 'recharts'

import type { LineWidget } from '../types'

interface WidgetLineProps {
  widget: LineWidget
  data?: unknown
  error?: string
}

interface LineDatum {
  t: string
  [metric: string]: number | string
}

function normalize(data: unknown, primary: string): LineDatum[] {
  if (!Array.isArray(data)) return []
  return data
    .map((row) => {
      if (!row || typeof row !== 'object') return null
      const obj = row as Record<string, unknown>
      const t = obj.t ?? obj.time ?? obj.hour ?? obj.day
      if (t == null) return null
      const out: LineDatum = { t: String(t) }
      for (const [k, v] of Object.entries(obj)) {
        if (k === 't' || k === 'time' || k === 'hour' || k === 'day') continue
        if (typeof v === 'number' && Number.isFinite(v)) out[k] = v
      }
      if (out[primary] == null && typeof obj[primary] === 'number') {
        out[primary] = obj[primary] as number
      }
      return out
    })
    .filter((x): x is LineDatum => x !== null)
}

const SERIES_COLORS = ['#0070ab', '#ed00be', '#fff662', '#00c7ca']

export function WidgetLine({ widget, data, error }: WidgetLineProps) {
  const rows = normalize(data, widget.metric)
  const allSeries = [widget.metric, ...widget.overlay]
  return (
    <div className="flex h-full flex-col rounded-xl border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <div className="mb-1 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
        {widget.title}
      </div>
      {error || rows.length === 0 ? (
        <div className="flex flex-1 items-center justify-center text-xs text-slate-400">
          {error ?? 'no data'}
        </div>
      ) : (
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={rows} margin={{ top: 4, right: 8, bottom: 8, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="t" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            {allSeries.map((series, i) => (
              <Line
                key={series}
                type="monotone"
                dataKey={series}
                stroke={SERIES_COLORS[i % SERIES_COLORS.length]}
                strokeWidth={2}
                dot={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  )
}
